import { useToggle } from "react-use";
import { MoreVertical, Pencil, Trash } from "lucide-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDeleteTemplateCategoryMutation } from "@/modules/snapshots/businessReports.api";
import { RenameCategoryModal } from "@/modules/snapshots/modals/renameCategoryModal";

interface Props {
  label: string;
  categoryId: string | null;
  isAdmin: boolean;
  count?: number;
  isSelected?: boolean;
  onSelect?: () => void;
  variant?: "section" | "sidebarRow";
}

const baseHeadingClassName = "mb-3 border-b border-[#ebe5de] pb-2 text-sm font-semibold text-gray-800";
const sidebarRowClassName = "border-t border-[#ebe5de] py-2.5 text-sm text-gray-700 first:border-t-0";

const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== "object" || error === null) return fallback;
  const candidate = error as {
    data?: { detail?: string };
    status?: string | number;
    error?: string;
    message?: string;
  };
  if (candidate.data?.detail) return candidate.data.detail;
  if (candidate.status === "FETCH_ERROR") return "Network error. Please check your connection and try again.";
  if (candidate.status === "TIMEOUT_ERROR") return "Request timed out. Please try again.";
  if (candidate.status === "PARSING_ERROR") return "Server returned an invalid response.";
  if (candidate.message) return candidate.message;
  return fallback;
};

export const CategoryHeader = ({
  label,
  categoryId,
  isAdmin,
  count,
  isSelected = false,
  onSelect,
  variant = "section",
}: Props) => {
  const canManage = isAdmin && categoryId !== null;
  const isSidebarRow = variant === "sidebarRow";

  const [isRenameOpen, toggleIsRenameOpen] = useToggle(false);
  const [isDeleteOpen, toggleIsDeleteOpen] = useToggle(false);

  const [deleteCategory, { isLoading: isDeleting }] = useDeleteTemplateCategoryMutation();

  const confirmDelete = async () => {
    if (!categoryId) return;
    try {
      await deleteCategory(categoryId).unwrap();
      toggleIsDeleteOpen(false);
      toast.success("Category deleted");
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "snapshot-categories", operation: "delete" },
        extra: { categoryId },
      });
      console.error("Failed to delete category", error);
      toast.error(apiErrorMessage(error, "Failed to delete category"));
    }
  };

  return (
    <div
      className={clsx(
        isSidebarRow ? sidebarRowClassName : baseHeadingClassName,
        "flex min-w-0 items-center justify-between gap-3",
        isSidebarRow && isSelected && "-mx-4 bg-[#f3eadf] px-4",
      )}
    >
      {isSidebarRow && onSelect ? (
        <button
          type="button"
          aria-pressed={isSelected}
          className={clsx("min-w-0 flex-1 truncate text-left", isSelected && "font-semibold text-[#111b35]")}
          onClick={onSelect}
        >
          {label}
        </button>
      ) : isSidebarRow ? (
        <span className="min-w-0 truncate">{label}</span>
      ) : (
        <h4 className="text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">{label}</h4>
      )}

      {(typeof count === "number" || canManage) && (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {typeof count === "number" && <span className="text-xs font-semibold text-gray-500">{count}</span>}
          {canManage && (
            <>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    aria-label={`Manage category ${label}`}
                    className={clsx(
                      "shrink-0 p-0 text-gray-500 hover:text-gray-800",
                      isSidebarRow ? "h-7 w-7" : "h-8 w-8",
                    )}
                  >
                    <MoreVertical size={isSidebarRow ? 16 : 18} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => toggleIsRenameOpen(true)} className="cursor-pointer">
                    <Pencil className="mr-2 h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => toggleIsDeleteOpen(true)}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <RenameCategoryModal
                open={isRenameOpen}
                onClose={() => toggleIsRenameOpen(false)}
                categoryId={categoryId}
                currentName={label}
              />

              <Dialog open={isDeleteOpen} onOpenChange={open => toggleIsDeleteOpen(open)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete category &ldquo;{label}&rdquo;?</DialogTitle>
                    <DialogDescription>
                      Templates in this category will become uncategorised. This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => toggleIsDeleteOpen(false)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                    <Button type="button" variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
                      {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      )}
    </div>
  );
};
